import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

const EmailVerificationForm = ({ email, fromLogin = false, onSuccess, onBackToLogin }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const { verifyEmail, resendVerificationCode } = useAuth();

  // Countdown para reenvio de código
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Digite o código de verificação');
      return;
    }

    if (code.length !== 6) {
      setError('O código deve ter 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifyEmail(email, code);
      
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onSuccess?.(result.user);
        }, 1500);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await resendVerificationCode(email);
      
      if (result.success) {
        setSuccess(result.message);
        setCountdown(60); // 60 segundos para reenvio
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Apenas números
    if (value.length <= 6) {
      setCode(value);
      setError('');
    }
  };

  return (
    <div className="auth-form">
      <div className="auth-header">
        <h2>Verificar Email</h2>
        <p>
          {fromLogin ? (
            <>
              Você precisa verificar seu email antes de fazer login.<br />
              Enviamos um novo código para<br />
              <strong>{email}</strong>
            </>
          ) : (
            <>
              Enviamos um código de 6 dígitos para<br />
              <strong>{email}</strong>
            </>
          )}
        </p>
      </div>

      {error && (
        <div className="auth-error">
          {error}
        </div>
      )}

      {success && (
        <div className="auth-success">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="auth-form-content">
        <div className="input-group">
          <label htmlFor="code">Código de Verificação</label>
          <input
            type="text"
            id="code"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            maxLength="6"
            className="verification-code-input"
            autoComplete="one-time-code"
            disabled={loading}
          />
          <small>Digite o código de 6 dígitos enviado para seu email</small>
        </div>

        <button 
          type="submit" 
          className="auth-button primary"
          disabled={loading || code.length !== 6}
        >
          {loading ? 'Verificando...' : 'Verificar Email'}
        </button>

        <div className="verification-actions">
          <p>Não recebeu o código?</p>
          
          {countdown > 0 ? (
            <button
              type="button"
              className="auth-button secondary"
              disabled
            >
              Reenviar em {countdown}s
            </button>
          ) : (
            <button
              type="button"
              className="auth-button secondary"
              onClick={handleResendCode}
              disabled={loading}
            >
              {loading ? 'Reenviando...' : 'Reenviar Código'}
            </button>
          )}
        </div>

        <div className="auth-footer">
          <button
            type="button"
            className="auth-link"
            onClick={onBackToLogin}
            disabled={loading}
          >
            ← Voltar ao Login
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailVerificationForm;
